import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Users, Inbox, CheckCircle2, Mail, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const statusColor = {
  new: "bg-gold/25 text-gold-dark",
  contacted: "bg-amber-100 text-amber-800",
  qualified: "bg-olive/20 text-olive",
  won: "bg-green-100 text-green-700",
  lost: "bg-red-100 text-red-600",
};

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (ready && !user) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const cards = stats
    ? [
        { icon: Inbox, label: "Total leads", value: stats.total_leads },
        { icon: Users, label: "New", value: stats.new_leads },
        { icon: CheckCircle2, label: "Won", value: stats.won_leads },
        { icon: Mail, label: "Subscribers", value: stats.subscribers },
      ]
    : [];

  return (
    <div className="min-h-screen bg-warm">
      <header className="bg-navy text-warm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo variant="light" showText={false} />
          <div className="flex items-center gap-3">
            <span className="text-sm text-warm/60 hidden sm:block">{user?.email}</span>
            <button onClick={load} data-testid="admin-refresh" className="rounded-full border border-warm/20 p-2 hover:bg-warm/10 transition-colors"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 rounded-full bg-warm/10 px-4 py-2 text-sm hover:bg-warm/20 transition-colors"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-playfair text-3xl text-navy">Leads Dashboard</h1>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl bg-white border border-navy/10 p-6">
              <c.icon className="h-6 w-6 text-gold" />
              <p className="font-playfair text-4xl text-navy mt-3">{c.value}</p>
              <p className="text-navy/50 text-sm mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl bg-white border border-navy/10 overflow-hidden">
          <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
            <h2 className="font-500 text-navy">All Leads ({leads.length})</h2>
          </div>
          {loading ? (
            <p className="p-10 text-center text-navy/40">Loading…</p>
          ) : leads.length === 0 ? (
            <p data-testid="no-leads" className="p-10 text-center text-navy/40">No leads yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-warm text-navy/50 text-left text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-6 py-3">Name</th>
                    <th className="px-6 py-3">Contact</th>
                    <th className="px-6 py-3">Interest</th>
                    <th className="px-6 py-3">Journey</th>
                    <th className="px-6 py-3">Timeline</th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-navy/5 hover:bg-warm/50">
                      <td className="px-6 py-4">
                        <p className="font-500 text-navy">{l.name}</p>
                        <p className="text-navy/40 text-xs">{l.country}</p>
                        {l.message && <p className="text-navy/50 text-xs mt-1 max-w-xs truncate">{l.message}</p>}
                      </td>
                      <td className="px-6 py-4 text-navy/70">
                        <p>{l.email}</p>
                        <p className="text-xs text-navy/40">{l.phone}</p>
                      </td>
                      <td className="px-6 py-4 text-navy/70">{l.interest || "—"}</td>
                      <td className="px-6 py-4 text-navy/70">{l.journey_type || "—"}</td>
                      <td className="px-6 py-4 text-navy/70">{l.timeline || "—"}</td>
                      <td className="px-6 py-4">
                        <select
                          data-testid={`lead-status-${l.id}`}
                          value={l.status}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                          className={`rounded-full px-3 py-1.5 text-xs font-500 border-none focus:outline-none cursor-pointer ${statusColor[l.status] || "bg-navy/10 text-navy"}`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
